const SESSION_KEY = 'chutex_admin_session';
const LEGACY_BASIC_KEY = 'chutex_admin_basic';

function clearLegacyBasic() {
  sessionStorage.removeItem(LEGACY_BASIC_KEY);
}

export function setChutexSession(token) {
  clearLegacyBasic();
  if (token) {
    sessionStorage.setItem(SESSION_KEY, token);
  } else {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

export function getChutexSession() {
  try {
    clearLegacyBasic();
    return sessionStorage.getItem(SESSION_KEY) || '';
  } catch {
    return '';
  }
}

export function getChutexAuthHeader() {
  const token = getChutexSession();
  if (!token) return null;
  return `Bearer ${token}`;
}

/** Alias de migration : n'émet plus de Basic depuis le navigateur. */
export function getChutexBasicHeader() {
  return getChutexAuthHeader();
}

export function clearChutexCredentials() {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem('chutex_session_role');
  clearLegacyBasic();
}

export function hasChutexCredentials() {
  return Boolean(getChutexSession() || getChutexRole() === 'ACCOUNTANT_READ_ONLY');
}

export function classifyClientStorageValue(key, value) {
  if (key === SESSION_KEY) return 'session_reference';
  if (key === LEGACY_BASIC_KEY) return 'credential';
  if (key === 'chutex_admin_portal_password') return 'credential';
  if (key === 'chutex_admin_portal_unlocked') return 'non_secret_marker';
  if (typeof value === 'string' && value.includes('"password"')) return 'credential';
  return 'unknown';
}

export async function rgpdLoginWithPassword(password) {
  const res = await fetch('/api/public/portal-auth/rgpd', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.detail || 'Mot de passe incorrect');
  }
  if (!data.token) {
    throw new Error('Session serveur absente');
  }
  setChutexSession(data.token);
  setChutexRole(data.role || 'ADMIN');
  return data;
}

export async function chutexLoginWithPassword(password) {
  const res = await fetch('/api/public/portal-auth/chutex', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.detail || 'Mot de passe incorrect');
  }
  if (!data.token) {
    throw new Error('Session serveur absente');
  }
  setChutexSession(data.token);
  setChutexRole(data.role || (data.owner_permission ? 'OWNER' : 'ADMIN'));
  return data;
}

export function setChutexRole(role) {
  if (role) sessionStorage.setItem('chutex_session_role', role);
  else sessionStorage.removeItem('chutex_session_role');
}

export function getChutexRole() {
  try {
    return sessionStorage.getItem('chutex_session_role') || '';
  } catch {
    return '';
  }
}

export function isAccountantOnly() {
  return getChutexRole() === 'ACCOUNTANT_READ_ONLY';
}

export async function comptaLoginWithPassword(password) {
  const res = await fetch('/api/public/portal-auth/compta', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.detail || 'Mot de passe incorrect');
  }
  // The accounting session is an HttpOnly cookie and is never readable here.
  setChutexSession('');
  setChutexRole(data.role || 'ACCOUNTANT_READ_ONLY');
  return data;
}

export async function verifyChutexSession() {
  const auth = getChutexAuthHeader();
  const me = await fetch('/api/admin/chutex/compta/me', {
    credentials: 'include',
    headers: auth ? { Authorization: auth } : {},
  });
  if (me.ok) {
    const data = await me.json().catch(() => ({}));
    if (data.role) setChutexRole(data.role);
    return true;
  }
  if (!auth) {
    if (!getChutexAuthHeader()) clearChutexCredentials();
    return false;
  }
  const res = await fetch('/api/admin/chutex/overview', {
    headers: { Authorization: auth },
  });
  if (!res.ok) {
    if (getChutexAuthHeader() === auth) {
      clearChutexCredentials();
      setChutexRole('');
    }
    return false;
  }
  return true;
}

export async function logoutComptaSession() {
  try {
    await fetch('/api/public/portal-auth/compta/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    clearChutexCredentials();
  }
}
